import React from 'react';
import { useIntl } from 'react-intl';
import { Row, Col } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import { TypedMemo } from '../../common/types';
import { useSettingsTabSelection } from './useSettingsTabSelection';
import { SMLSettingsTabId } from '../tabs/types';
import { SubscriptionTab } from '../tabs/SubscriptionTab';
import { AccountTab } from '../tabs/AccountTab';
import { ArtistTab } from '../tabs/artist/ArtistTab';
import { SmartLinksTab } from '../tabs/smartlinks/SmartLinksTab';
import { TeamTab } from '../../team/TeamTab';
import { NotificationsTab } from '../tabs/NotificationsTab';
import { SetupTab } from '../tabs/SetupTab';
import { AiAssistantsTab } from '../tabs/AiAssistantsTab';
import { ApiTab } from '../tabs/ApiTab';
import { LegalTab } from '../tabs/LegalTab';
import { SupportTab } from '../tabs/SupportTab';
import { OutreachTab } from '../tabs/outreach/OutreachTab';
import { ConnectionsTab } from '../tabs/connections/ConnectionsTab';
import { settingsCommonMessages } from '../tabs/settingsCommonMessages';
import { SettingsRail } from './SettingsRail';
import { SettingsMobileIndex } from './SettingsMobileIndex';
import { useSettingsTabBadges } from './useSettingsTabBadges';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import styles from './SMLSettingsViewRenderer.module.scss';
/* eslint-enable css-modules/no-unused-class */

const TAB_BODIES: Partial<Record<SMLSettingsTabId, React.FC>> = {
  [SMLSettingsTabId.subscription]: SubscriptionTab,
  [SMLSettingsTabId.account]: AccountTab,
  [SMLSettingsTabId.artist]: ArtistTab,
  [SMLSettingsTabId.smartlinks]: SmartLinksTab,
  [SMLSettingsTabId.team]: TeamTab,
  [SMLSettingsTabId.connections]: ConnectionsTab,
  [SMLSettingsTabId.notifications]: NotificationsTab,
  [SMLSettingsTabId.outreach]: OutreachTab,
  [SMLSettingsTabId.setup]: SetupTab,
  [SMLSettingsTabId.aiAssistants]: AiAssistantsTab,
  [SMLSettingsTabId.api]: ApiTab,
  [SMLSettingsTabId.legal]: LegalTab,
  [SMLSettingsTabId.support]: SupportTab,
};

/**
 * Settings (design settings-redesign a1, a2). Every tab shows on every plan: a tab the plan cannot
 * use explains itself. From md up: the rail and the pane. Below md: an index of the tabs, and a tab
 * with a "Settings" back link once `?tab=` names one. The page head (`SettingsPageHead`) names the tab.
 */
const SMLSettingsViewRendererComponent: React.FC = () => {
  const intl = useIntl();
  const { tabs, requestedTab, activeTab, selectFromRail, selectFromIndex, backToIndex } = useSettingsTabSelection();
  const badges = useSettingsTabBadges();

  const ActiveBody = TAB_BODIES[activeTab] ?? AccountTab;
  const showIndexOnPhone = requestedTab === null;

  return (
    <Row>
      <Col md={3} className="mb-4 d-none d-md-block">
        <SettingsRail tabs={tabs} activeTab={activeTab} badges={badges} onSelect={selectFromRail} />
      </Col>
      {showIndexOnPhone && (
        <Col xs={12} className="d-md-none mb-4">
          <SettingsMobileIndex tabs={tabs} onSelect={selectFromIndex} />
        </Col>
      )}
      <Col md={9} className={`${styles.contentPane}${showIndexOnPhone ? ' d-none d-md-block' : ''}`}>
        {!showIndexOnPhone && (
          <button type="button" className={`${styles.backLink} d-md-none`} onClick={backToIndex}>
            <FontAwesomeIcon icon={faChevronLeft} />
            {intl.formatMessage(settingsCommonMessages.backToSettings)}
          </button>
        )}
        <ActiveBody />
      </Col>
    </Row>
  );
};

export const SMLSettingsViewRenderer = TypedMemo(SMLSettingsViewRendererComponent);
